import React from 'react';
import {
    DndContext,
    DragEndEvent,
    useSensor,
    useSensors,
    PointerSensor
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    horizontalListSortingStrategy
} from '@dnd-kit/sortable';
import { Table } from '@tanstack/react-table';

interface HBTableDndProviderProps<T> {
    table: Table<T>;
    onColumnOrderChange?: (newColumnOrder: string[]) => void;
    children: React.ReactNode;
}

function HBTableDndProvider<T>({
    table,
    onColumnOrderChange,
    children
}: HBTableDndProviderProps<T>) {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 3
            }
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const currentOrder = table.getState().columnOrder;
        const oldIndex = currentOrder.indexOf(String(active.id));
        const newIndex = over.data?.current?.overIndex;

        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);

        table.setColumnOrder(newOrder);
        table.options.state.columnOrder = newOrder;
        onColumnOrderChange?.(newOrder);
    };

    const columnIds = table.getState().columnOrder;

    return (
        <DndContext
            onDragEnd={handleDragEnd}
            sensors={sensors}
            modifiers={[restrictToHorizontalAxis]}
            autoScroll={{
                threshold: { x: 0, y: 0 },
                acceleration: 10
            }}
        >
            <SortableContext
                items={columnIds}
                strategy={horizontalListSortingStrategy}
            >
                {children}
            </SortableContext>
        </DndContext>
    );
}

export default HBTableDndProvider;
